import type { GuidedDraft } from './guided'
import { clampSliderValue } from './sizing'

export const GIB = 1024 ** 3

export type CapacityVector = {
  cpuMilli: number | null
  memoryBytes: number | null
  storageBytes: number | null
  gpuUnits: number | null
}

export type EnvelopeResolution = 'provider' | 'node'

export type CapacityEnvelope = {
  resolution: EnvelopeResolution
  dataAvailable: boolean
  observedHosts: number
  staleHosts: number
  max: CapacityVector
  floor: CapacityVector
  hosts: CapacityVector[]
  /**
   * Parallel to `hosts`: which provider each row belongs to, as an opaque key.
   * At provider resolution one provider can contribute several rows (listed
   * nodes plus an upper-bound row), so a count of providers is a count of
   * distinct keys, not of rows.
   */
  hostGroups?: string[]
}

export type SliderBound = { min: number; max: number; step: number }
export type SizeBounds = { cpu: SliderBound; ram: SliderBound; storage: SliderBound }

export const DEFAULT_SIZE_BOUNDS: SizeBounds = {
  cpu: { min: 250, max: 8000, step: 250 },
  ram: { min: 1, max: 64, step: 1 },
  storage: { min: 1, max: 500, step: 1 },
}

/** Align an observed capacity down to the same steps used by the form. */
export function boundFor(available: number | null, base: SliderBound): SliderBound {
  if (available === null) return base
  const aligned = Math.max(0, Math.floor(available / base.step) * base.step)
  if (aligned >= base.min) return { ...base, max: aligned }
  // A reported zero (or a positive amount below the first normal step) is a
  // real bound, not an invitation to restore the unbounded default range.
  return { min: aligned, max: aligned, step: base.step }
}

/** Convert a provider/node envelope maximum to the form's slider units. */
export function poolSizeBounds(envelope: CapacityEnvelope | null | undefined): SizeBounds | null {
  if (!envelope?.dataAvailable) return null
  return {
    cpu: boundFor(envelope.max.cpuMilli, DEFAULT_SIZE_BOUNDS.cpu),
    ram: boundFor(envelope.max.memoryBytes === null ? null : Math.floor(envelope.max.memoryBytes / GIB), DEFAULT_SIZE_BOUNDS.ram),
    storage: boundFor(envelope.max.storageBytes === null ? null : Math.floor(envelope.max.storageBytes / GIB), DEFAULT_SIZE_BOUNDS.storage),
  }
}

export type RequestedCapacity = CapacityVector

/** The chain's one-provider placement rule uses every replica's resources. */
export function normalizedReplicaCount(draft: Pick<GuidedDraft, 'recoveryMode' | 'replicaCount'>): number {
  if (draft.recoveryMode !== 'replicated') return 1
  return Math.max(2, Math.min(10, Number.isInteger(draft.replicaCount) ? draft.replicaCount : 2))
}

function axisValue(vector: CapacityVector, axis: ResourceAxis): number | null {
  switch (axis) {
    case 'cpu': return vector.cpuMilli
    case 'memory': return vector.memoryBytes
    case 'storage': return vector.storageBytes
    case 'gpu': return vector.gpuUnits
  }
}

export type ResourceAxis = 'cpu' | 'memory' | 'storage' | 'gpu'
export const RESOURCE_AXES: readonly ResourceAxis[] = ['cpu', 'memory', 'storage', 'gpu']

function canFit(host: CapacityVector, requested: RequestedCapacity, replicas: number): boolean {
  return RESOURCE_AXES.every((axis) => {
    const available = axisValue(host, axis)
    const required = axisValue(requested, axis)
    if (required === 0) return true
    return available !== null && required !== null && available >= required * replicas
  })
}

/** Count hosts/providers that can hold the entire group on one host/provider. */
export function feasibleHostCount(hosts: readonly CapacityVector[], requested: RequestedCapacity, replicas = 1): number {
  const count = Number.isInteger(replicas) && replicas > 0 ? replicas : 1
  return hosts.filter((host) => canFit(host, requested, count)).length
}

/**
 * How many distinct providers have a row that fits. `groups` runs parallel to
 * `hosts`; a row without a group counts as its own provider.
 */
export function feasibleProviderCount(hosts: readonly CapacityVector[], groups: readonly string[] | undefined, requested: RequestedCapacity, replicas = 1): number {
  const count = Number.isInteger(replicas) && replicas > 0 ? replicas : 1
  const fitting = new Set<string>()
  hosts.forEach((host, index) => {
    if (canFit(host, requested, count)) fitting.add(groups?.[index] ?? `row:${index}`)
  })
  return fitting.size
}

/** The count the page states: nodes at node resolution, providers at provider resolution. */
export function feasibleCount(envelope: Pick<CapacityEnvelope, 'resolution' | 'hosts' | 'hostGroups'>, requested: RequestedCapacity, replicas = 1): number {
  return envelope.resolution === 'provider'
    ? feasibleProviderCount(envelope.hosts, envelope.hostGroups, requested, replicas)
    : feasibleHostCount(envelope.hosts, requested, replicas)
}

/** Axes for which no observed host has enough capacity, useful refusal copy. */
export function limitingAxes(hosts: readonly CapacityVector[], requested: RequestedCapacity, replicas = 1): ResourceAxis[] {
  const count = Number.isInteger(replicas) && replicas > 0 ? replicas : 1
  return RESOURCE_AXES.filter((axis) => {
    const required = axisValue(requested, axis)
    if (required === null || required === 0) return false
    return !hosts.some((host) => {
      const available = axisValue(host, axis)
      return available !== null && available >= required * count
    })
  })
}

/**
 * Convert the guided form's GB values into a per-replica capacity vector.
 * A persistent volume is disk on the same host, so it counts as storage: the
 * provider sums every storage entry when it checks a rental against free disk.
 */
export function requestedCapacityForDraft(draft: Pick<GuidedDraft, 'cpu' | 'ram' | 'storage' | 'volume'>): RequestedCapacity {
  return { cpuMilli: draft.cpu, memoryBytes: draft.ram * GIB, storageBytes: totalStorageGi(draft) * GIB, gpuUnits: 0 }
}

/** The instance disk plus the persistent volume, in GB: what the host must have free. */
export function totalStorageGi(draft: Pick<GuidedDraft, 'storage' | 'volume'>): number {
  return draft.storage + (draft.volume?.sizeGi ?? 0)
}

/** Keep a draft's resources inside the form's current bounds; unchanged drafts keep their identity. */
export function clampDraftResources<T extends Pick<GuidedDraft, 'cpu' | 'ram' | 'storage'>>(draft: T, bounds: SizeBounds): T {
  const cpu = clampSliderValue(draft.cpu, bounds.cpu)
  const ram = clampSliderValue(draft.ram, bounds.ram)
  const storage = clampSliderValue(draft.storage, bounds.storage)
  if (cpu === draft.cpu && ram === draft.ram && storage === draft.storage) return draft
  return { ...draft, cpu, ram, storage }
}
